import { computed, ref } from '@vue/composition-api'
import { sharedCtx } from '../context'
import { ICompanyCreditsDetailResponse, IPersonalCreditsDetailResponse } from '../service/apis/creditsApi'
import { IUserUsageFrequency, IUserUsageInfo } from '../types/account'
import { useAuthStore } from './useAuthStore'

export type IHiroUsageCount = {
  mode: 'fast' | 'thinking'
  current: number
  total: number
  frequency?: IUserUsageFrequency
}

export type IHiroCompanyCreditsUsage =
  | {
      accountType: 'company'
      mode: 'daily_limit'
      used: number
      total: number
    }
  | {
      accountType: 'company'
      mode: 'shared_pool'
    }

export type IHiroPersonalCreditsUsage = {
  accountType: 'personal'
  mode: 'balance'
  available: number
  expiringItems: Array<{
    available: number
    endAt: number
    chargeMode: string
  }>
}

export type IHiroUsageDetail = {
  uses: IHiroUsageCount[]
  credits?: IHiroCompanyCreditsUsage | IHiroPersonalCreditsUsage
}

function normalizeUses(usageInfo: IUserUsageInfo | null): IHiroUsageCount[] {
  const uses: IHiroUsageCount[] = []
  const fastUsage = usageInfo?.hiro_ls
  const thinkingUsage = usageInfo?.hiro_ls_deep

  if (fastUsage?.total && fastUsage.total > 0) {
    uses.push({
      mode: 'fast',
      current: fastUsage.current,
      total: fastUsage.total,
      frequency: fastUsage.type,
    })
  }

  if (thinkingUsage?.total && thinkingUsage.total > 0) {
    uses.push({
      mode: 'thinking',
      current: thinkingUsage.current,
      total: thinkingUsage.total,
      frequency: thinkingUsage.type,
    })
  }

  return uses
}

function normalizeCompanyCredits(data: ICompanyCreditsDetailResponse): IHiroCompanyCreditsUsage {
  const dailyLimit = data.user_daily_limit || data.company_user_daily_limit || 0

  if (dailyLimit > 0) {
    return {
      accountType: 'company',
      mode: 'daily_limit',
      used: Math.max(0, (data.daily_used_credits || 0) - (data.frozen_credits || 0)),
      total: dailyLimit,
    }
  }

  return {
    accountType: 'company',
    mode: 'shared_pool',
  }
}

function normalizePersonalCredits(data: IPersonalCreditsDetailResponse): IHiroPersonalCreditsUsage {
  const now = Date.now()
  const expiringItems = (data.items || [])
    .map((item) => ({
      available: Math.max(0, item.credits - (item.used_credits || 0)),
      endAt: item.end_at,
      chargeMode: item.charge_mode,
    }))
    .filter((item) => item.endAt >= now && item.available > 0)
    .sort((a, b) => a.endAt - b.endAt)

  return {
    accountType: 'personal',
    mode: 'balance',
    available: Math.max(0, (data.total_credits || 0) - (data.used_credits || 0) - (data.frozen_credits || 0)),
    expiringItems,
  }
}

export function useHiroUsage() {
  const {
    state: { boProfileInfo, creditsControl, usageInfo },
    actions: { syncUserUsage },
  } = useAuthStore()
  const usageDetail = ref<IHiroUsageDetail>({ uses: [] })
  const isLoading = ref(false)

  const hasCreditsPackage = computed(() => creditsControl.value?.credits_package === true)

  let loadingPromise: Promise<IHiroUsageDetail> | null = null

  async function loadUses(): Promise<IHiroUsageCount[]> {
    try {
      await syncUserUsage()
      return normalizeUses(usageInfo.value)
    } catch {
      return []
    }
  }

  async function loadCredits(): Promise<IHiroUsageDetail['credits']> {
    if (!hasCreditsPackage.value) {
      return undefined
    }

    try {
      if (boProfileInfo.value?.company_id) {
        const response = await sharedCtx.service.credits.getCompanyCreditsDetail()
        return response.success && response.data ? normalizeCompanyCredits(response.data) : undefined
      }

      const response = await sharedCtx.service.credits.getPersonalCreditsDetail()
      return response.success && response.data ? normalizePersonalCredits(response.data) : undefined
    } catch {
      return undefined
    }
  }

  async function requestUsage(): Promise<IHiroUsageDetail> {
    isLoading.value = true
    usageDetail.value = { uses: [] }

    try {
      const [uses, credits] = await Promise.all([loadUses(), loadCredits()])
      usageDetail.value = {
        uses,
        credits,
      }
      return usageDetail.value
    } finally {
      isLoading.value = false
    }
  }

  function loadUsage() {
    if (loadingPromise) {
      return loadingPromise
    }

    loadingPromise = requestUsage().finally(() => {
      loadingPromise = null
    })
    return loadingPromise
  }

  return {
    usageDetail,
    isLoading,
    loadUsage,
  }
}
